// =============================================================================
// VINUM - Router Configuration
// Migrated from Flutter AppRoutes with EXACT route structure
// =============================================================================

import React, { Suspense, lazy } from 'react';
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
  Outlet,
} from 'react-router-dom';
import { AppShell } from '@/layouts/AppShell';
import { AdminShell } from '@/layouts/AdminShell';
import { PageLoading } from '@/components/ui/Spinner';
import { PageTransition } from '@/components/ui/PageTransition';
import {
  useAuthStore,
  ROUTES,
  selectIsAuthenticated,
  selectHasManagerRole,
  selectHasFullAccess,
  selectCanAccessWineryArea,
} from '@vinum/shared';

// =============================================================================
// Lazy-loaded Pages
// =============================================================================

// Auth pages
const LoginPage = lazy(() => import('@/pages/auth/LoginPage'));
const RegisterPage = lazy(() => import('@/pages/auth/RegisterPage'));
const ForgotPasswordPage = lazy(() => import('@/pages/auth/ForgotPasswordPage'));
const RequestResetPage = lazy(() => import('@/pages/auth/RequestResetPage'));
const ResetPasswordCodePage = lazy(() => import('@/pages/auth/ResetPasswordCodePage'));
const ResetPasswordPage = lazy(() => import('@/pages/auth/ResetPasswordPage'));
const LimitedAccessPage = lazy(() => import('@/pages/auth/LimitedAccessPage'));

// Main pages
const HomePage = lazy(() => import('@/pages/HomePage'));
const CatalogPage = lazy(() => import('@/pages/catalog/CatalogPage'));
const FavoritesPage = lazy(() => import('@/pages/catalog/FavoritesPage'));
const WineDetailPage = lazy(() => import('@/pages/catalog/WineDetailPage'));
const CartPage = lazy(() => import('@/pages/cart/CartPage'));
const CheckoutPage = lazy(() => import('@/pages/checkout/CheckoutPage'));
const OrdersPage = lazy(() => import('@/pages/orders/OrdersPage'));
const OrderDetailPage = lazy(() => import('@/pages/orders/OrderDetailPage'));
const ScarichiPage = lazy(() => import('@/pages/scarichi/ScarichiPage'));
const ProfilePage = lazy(() => import('@/pages/profile/ProfilePage'));
const AccountSettingsPage = lazy(() => import('@/pages/profile/AccountSettingsPage'));
const NotificationsPage = lazy(() => import('@/pages/profile/NotificationsPage'));
const WineryPage = lazy(() => import('@/pages/winery/WineryPage'));
const StatisticsPage = lazy(() => import('@/pages/admin/StatisticsPage'));
// NB: StatisticsSalesPage non viene piu' montata direttamente nel router.
// E' un tab interno di AdminAnalyticsPage (vedi /admin/analytics ?tab=sales).
// La rotta /statistics/sales sopravvive solo come redirect per link legacy.

// Admin pages (CMS)
const AdminDashboard = lazy(() => import('@/pages/admin/AdminDashboard'));
const AdminUsersPage = lazy(() => import('@/pages/admin/users/AdminUsersPage'));
const AdminContentPage = lazy(() => import('@/pages/admin/content/AdminContentPage'));
const AdminSettingsPage = lazy(() => import('@/pages/admin/settings/AdminSettingsPage'));
const AdminCatalogPage = lazy(() => import('@/pages/admin/catalog/AdminCatalogPage'));
const AdminOrdersPage = lazy(() => import('@/pages/admin/orders/AdminOrdersPage'));
const AdminAnalyticsPage = lazy(() => import('@/pages/admin/analytics/AdminAnalyticsPage'));
const AdminAuditLogPage = lazy(() => import('@/pages/admin/audit/AdminAuditLogPage'));
const AdminNotificationsPage = lazy(() => import('@/pages/admin/notifications/AdminNotificationsPage'));
const AdminWineriesPage = lazy(() => import('@/pages/admin/wineries/AdminWineriesPage'));
const AdminStockOutputsPage = lazy(() => import('@/pages/admin/stock-outputs/AdminStockOutputsPage'));
const AdminPlaceholderPage = lazy(() => import('@/pages/admin/AdminPlaceholderPage'));
const AdminUserDetailPage = lazy(() => import('@/pages/admin/users/AdminUserDetailPage'));
const NotFoundPage = lazy(() => import('@/pages/NotFoundPage'));

// =============================================================================
// Auth Guard
// =============================================================================

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  return <>{children}</>;
};

const RequireGuest: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);

  if (isAuthenticated) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <>{children}</>;
};

/**
 * RequireAdmin - Admin Panel Access Guard
 * Verifies user has Manager/Admin role before allowing access to admin routes
 */
const RequireAdmin: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const hasManagerRole = useAuthStore(selectHasManagerRole);

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (!hasManagerRole) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <>{children}</>;
};

/**
 * RequireFullAccess - Full Access Guard
 * Verifies user has full access (associated Customer or Admin/Manager role)
 * Redirects to LIMITED_ACCESS page if user has limited access
 */
const RequireFullAccess: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const isAuthenticated = useAuthStore(selectIsAuthenticated);
  const hasFullAccess = useAuthStore(selectHasFullAccess);

  if (!isAuthenticated) {
    return <Navigate to={ROUTES.LOGIN} replace />;
  }

  if (!hasFullAccess) {
    return <Navigate to={ROUTES.LIMITED_ACCESS} replace />;
  }

  return <>{children}</>;
};

/**
 * RequireWineryArea - Guard per pagine "area cantina" (/winery, /statistics/*).
 * Regola:
 *  - Manager/Admin → sempre accesso
 *  - Privato → mai accesso (anche con Customer associato), redirect a Home
 *  - Azienda → accesso se hasCustomer (logica preesistente)
 * Tutta la logica e' in selectCanAccessWineryArea (auth.store).
 */
const RequireWineryArea: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const canAccess = useAuthStore(selectCanAccessWineryArea);

  if (!canAccess) {
    return <Navigate to={ROUTES.HOME} replace />;
  }

  return <>{children}</>;
};

// =============================================================================
// Suspense Wrapper
// =============================================================================

const SuspenseWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Suspense fallback={<PageLoading message="Caricamento..." />}>
    <PageTransition>
      {children}
    </PageTransition>
  </Suspense>
);

// =============================================================================
// Router Configuration
// =============================================================================

export const router = createBrowserRouter([
  // Auth routes (no shell)
  {
    path: ROUTES.LOGIN,
    element: (
      <RequireGuest>
        <SuspenseWrapper>
          <LoginPage />
        </SuspenseWrapper>
      </RequireGuest>
    ),
  },
  {
    path: ROUTES.REGISTER,
    element: (
      <RequireGuest>
        <SuspenseWrapper>
          <RegisterPage />
        </SuspenseWrapper>
      </RequireGuest>
    ),
  },
  {
    path: ROUTES.FORGOT_PASSWORD,
    element: (
      <RequireGuest>
        <SuspenseWrapper>
          <ForgotPasswordPage />
        </SuspenseWrapper>
      </RequireGuest>
    ),
  },
  {
    path: ROUTES.REQUEST_RESET,
    element: (
      <RequireGuest>
        <SuspenseWrapper>
          <RequestResetPage />
        </SuspenseWrapper>
      </RequireGuest>
    ),
  },
  {
    path: ROUTES.RESET_PASSWORD_CODE,
    element: (
      <RequireGuest>
        <SuspenseWrapper>
          <ResetPasswordCodePage />
        </SuspenseWrapper>
      </RequireGuest>
    ),
  },
  {
    path: ROUTES.RESET_PASSWORD,
    element: (
      <RequireGuest>
        <SuspenseWrapper>
          <ResetPasswordPage />
        </SuspenseWrapper>
      </RequireGuest>
    ),
  },
  {
    path: ROUTES.AWAIT_APPROVAL,
    element: (
      <SuspenseWrapper>
        <div className="flex items-center justify-center min-h-screen">
          <div className="text-center p-8">
            <h1 className="text-2xl font-bold mb-4">Account in attesa di approvazione</h1>
            <p className="text-gray-500">
              Il tuo account è in fase di revisione. Riceverai una notifica quando sarà approvato.
            </p>
          </div>
        </div>
      </SuspenseWrapper>
    ),
  },
  {
    path: ROUTES.LIMITED_ACCESS,
    element: (
      <RequireAuth>
        <SuspenseWrapper>
          <LimitedAccessPage />
        </SuspenseWrapper>
      </RequireAuth>
    ),
  },

  // =============================================================================
  // Admin Panel Routes (separate layout, Admin only)
  // =============================================================================
  {
    path: '/admin',
    element: (
      <RequireAdmin>
        <AdminShell />
      </RequireAdmin>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <AdminDashboard />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'users',
        element: (
          <SuspenseWrapper>
            <AdminUsersPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'users/:userId',
        element: (
          <SuspenseWrapper>
            <AdminUserDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'content',
        element: (
          <SuspenseWrapper>
            <AdminContentPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'content/:contentId',
        element: (
          <SuspenseWrapper>
            <AdminPlaceholderPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'catalog',
        element: (
          <SuspenseWrapper>
            <AdminCatalogPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'orders',
        element: (
          <SuspenseWrapper>
            <AdminOrdersPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'notifications',
        element: (
          <SuspenseWrapper>
            <AdminNotificationsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'wineries',
        element: (
          <SuspenseWrapper>
            <AdminWineriesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'analytics',
        element: (
          <SuspenseWrapper>
            <AdminAnalyticsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'stock-outputs',
        element: (
          <SuspenseWrapper>
            <AdminStockOutputsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'settings',
        element: (
          <RequireAdmin>
            <SuspenseWrapper>
              <AdminSettingsPage />
            </SuspenseWrapper>
          </RequireAdmin>
        ),
      },
      {
        path: 'audit',
        element: (
          <SuspenseWrapper>
            <AdminAuditLogPage />
          </SuspenseWrapper>
        ),
      },
    ],
  },

  // =============================================================================
  // User App Routes (with AppShell)
  // Requires full access (associated Customer or Admin/Manager role)
  // =============================================================================
  {
    path: '/',
    element: (
      <RequireFullAccess>
        <AppShell />
      </RequireFullAccess>
    ),
    children: [
      {
        index: true,
        element: (
          <SuspenseWrapper>
            <HomePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'catalog',
        element: (
          <SuspenseWrapper>
            <CatalogPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'favorites',
        element: (
          <SuspenseWrapper>
            <FavoritesPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'wine/:itemId',
        element: (
          <SuspenseWrapper>
            <WineDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'cart',
        element: (
          <SuspenseWrapper>
            <CartPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'checkout',
        element: (
          <SuspenseWrapper>
            <CheckoutPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'orders',
        element: (
          <SuspenseWrapper>
            <OrdersPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'orders/:orderId',
        element: (
          <SuspenseWrapper>
            <OrderDetailPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'scarichi',
        element: (
          <SuspenseWrapper>
            <ScarichiPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'profile',
        element: (
          <SuspenseWrapper>
            <ProfilePage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'profile/settings',
        element: (
          <SuspenseWrapper>
            <AccountSettingsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'profile/notifications',
        element: (
          <SuspenseWrapper>
            <NotificationsPage />
          </SuspenseWrapper>
        ),
      },
      {
        path: 'winery',
        element: (
          <RequireWineryArea>
            <SuspenseWrapper>
              <WineryPage />
            </SuspenseWrapper>
          </RequireWineryArea>
        ),
      },
      {
        path: 'statistics',
        element: (
          <RequireWineryArea>
            <Outlet />
          </RequireWineryArea>
        ),
        children: [
          {
            index: true,
            element: <Navigate to="products" replace />,
          },
          {
            path: 'products',
            element: (
              <SuspenseWrapper>
                <StatisticsPage />
              </SuspenseWrapper>
            ),
          },
          {
            path: 'sales',
            element: <Navigate to={`${ROUTES.ADMIN_ANALYTICS}?tab=sales`} replace />,
          },
        ],
      },
    ],
  },

  // 404 - Not Found
  {
    path: '*',
    element: (
      <SuspenseWrapper>
        <NotFoundPage />
      </SuspenseWrapper>
    ),
  },
]);

// =============================================================================
// Router Component
// =============================================================================

export const AppRouter: React.FC = () => {
  return <RouterProvider router={router} />;
};
